import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import aptiveUserAccountId from 'app-recoil/selectors/aptive-user-account-id';
import { useFeatureFlag } from 'configcat-react';
import { customerInfoDataState } from 'app-recoil/atoms/customer-info';

const useIsSchedulingPreferencesEnabled = () => {
  const accountId = useRecoilValue(aptiveUserAccountId);
  const customerInfo = useRecoilValue(customerInfoDataState);

  const {
    value: whichOfficesAreEnabledForSchedulingPreference, 
    loading: whichOfficesAreEnabledForSchedulingPreferenceLoading
  } = useFeatureFlag('whichOfficesAreEnabledForSchedulingPreference', '');

  const {
    value: whichDateCutsOffSchedulingPreference,
    loading: whichDateCutsOffSchedulingPreferenceLoading
  } = useFeatureFlag('whichDateCutsOffSchedulingPreference', '');

  const officesEnabledForSchedulingPreference = useMemo(
    () => (whichOfficesAreEnabledForSchedulingPreference as string).split(',').map((office) => Number(office.trim())),
    [whichOfficesAreEnabledForSchedulingPreference]
  );

  const isSchedulingPreferencesEnabled = useMemo(
    () => {
      try {
        if (customerInfo && accountId && whichDateCutsOffSchedulingPreference) {
          return officesEnabledForSchedulingPreference.includes(customerInfo.officeId)
            && Number(accountId) % 2 !== 0 // Check if accountId is odd
            && new Date(customerInfo.dateAdded) <= new Date(whichDateCutsOffSchedulingPreference as string); // Check if dateAdded is before or equal to the cutoff date
        }
  
        return false;
      }
      catch (error) {
        window.console.error('Error parsing scheduling preference flags:', error);
        return false;
      }
    },
    [
      customerInfo, 
      officesEnabledForSchedulingPreference, 
      whichDateCutsOffSchedulingPreference,
      accountId
    ]
  );

  const isSchedulingPreferencesEnabledLoading = useMemo(
    () => !customerInfo
        || whichOfficesAreEnabledForSchedulingPreferenceLoading 
        || whichDateCutsOffSchedulingPreferenceLoading,
    [
        customerInfo,
        whichOfficesAreEnabledForSchedulingPreferenceLoading,
        whichDateCutsOffSchedulingPreferenceLoading
    ]
  );
  
  return { 
    isSchedulingPreferencesEnabled, 
    isSchedulingPreferencesEnabledLoading 
  };
};

export default useIsSchedulingPreferencesEnabled;
